import {TDetailPackage} from "@COMPONENTS/product/types";
import {EventDetail, eventDetailSSR} from "@QUERIES/prdt/event/useEventDetail";
import {goodsDetailSSR} from "@QUERIES/prdt/goods/useGoodsDetail";
import {GoodsImage, goodsImageSSR} from "@QUERIES/prdt/goods/useGoodsImage";
import {goodsVideoSSR} from "@QUERIES/prdt/goods/useGoodsVideo";
import {useRouter} from "next/router";
import {GetServerSidePropsContext} from "next/types";
import {useEffect} from "react";

/** 컴포넌트 */
import {useTaggingClick, useTaggingLoad} from "@/hooks/common/useTagging";
import {getCookie, getQueryParams} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {goodsTpLabel} from "@COMPONENTS/common/goodsUtil";
import Footer from "@COMPONENTS/layout/footer/Mobile";
import Header from "@COMPONENTS/layout/header/Mobile";
import TaggingGTM from "@COMPONENTS/product/pages/detailPackage/common/GTMTags";
import DetailBanner from "@COMPONENTS/product/pages/detailPackage/mobile/detailBanner";
import DetailCoupon from "@COMPONENTS/product/pages/detailPackage/mobile/detailCoupon";
import DetailHeader from "@COMPONENTS/product/pages/detailPackage/mobile/detailHeader";
import DetailPrice from "@COMPONENTS/product/pages/detailPackage/mobile/detailPrice";
import DetailPromotion from "@COMPONENTS/product/pages/detailPackage/mobile/detailPromotion";
import DetailRecommendProduct from "@COMPONENTS/product/pages/detailPackage/mobile/detailRecommendProduct";
import DetailSNB from "@COMPONENTS/product/pages/detailPackage/mobile/detailSNB";
import DetailSchedule from "@COMPONENTS/product/pages/detailPackage/mobile/detailSchedule";
import DetailScheduleSummary from "@COMPONENTS/product/pages/detailPackage/mobile/detailScheduleSummary";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import getSEO from "@FRAMEWORK/utils/seo";
import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import {goodsRecentEventSSR} from "@QUERIES/prdt/goods/useGoodsRecentEvent";
import {useQueryClient} from "@tanstack/react-query";
import dayjs from "dayjs";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import goodsLowestPriceEventSSR from "@QUERIES/prdt/goods/useGoodsLowestPriceEvent";

/* 최근 본 상품 저장 **/
const daykor = ["일", "월", "화", "수", "목", "금", "토"];

const Page = (props: TDetailPackage) => {
	/** 예외처리 */
	useEffect(() => {
		if (props.message) {
			alert(props.message);
			location.href = `${publicRuntimeConfig.ENV_PORTAL_URL}`;
		}
	}, []);
	if (props.message) return <></>;
	/** 정상 */
	const {
		query: {evCd, goodsCd, dspSid, menu},
		eventDetail,
		goodsImage,
	} = props;
	const router = useRouter();

	const mmdd = evCd.split("-")[1].substring(2, 6);

	useEffect(() => {
		setTimeout(() => {
			window.scrollTo({top: 0, behavior: "smooth"});
		}, 100);
	}, [router]);

	/** 로그인 정보 */
	const signin = useCustomerMe();
	const webCustNo = signin.data?.body?.webCustNo || "";

	const queryClient = useQueryClient();

	/** 최근 본 상품 SET */
	const setRecently = async (eventDetail: EventDetail, goodsImage: GoodsImage[], webCustId = "") => {
		const userId = getCookie("userId");
		if (!userId) return;

		const {evCd, evStartDt, outDeprtTm, evArriveDt, inArrvTm, trCompanySnm, trCompanyNm, trTp} = eventDetail;
		const {pathname} = location;
		const queries = getQueryParams();
		const search = `?menu=${queries.menu}&dspSid=${queries.dspSid}&evCd=${evCd}`;
		const detailedUrl = `${publicRuntimeConfig.ENV_PRODUCT_URL}${pathname.replace(/\.m/i, "")}${search}`;
		const imgUrl = goodsImage[0]?.imageThum3 || "";
		const goodsId = eventDetail.evCd ?? "";
		const goodsName = eventDetail.evNm ?? "";
		const type = eventDetail.goodsTp === "D" ? "DMT" : "PKG";
		const viewDate = dayjs().format("YYYY.MM.DD HH:mm:ss");
		const category = goodsTpLabel(eventDetail.goodsTp).join(";");
		const soldoutYn = "";

		const trafficCode = {A: trCompanySnm, T: "TRAIN", S: "SHIP", B: "BUS"}[trTp] || "ETC";
		const dayOfWeekStart = daykor[dayjs(evStartDt).day()];
		const dayOfWeekArrive = daykor[dayjs(evArriveDt).day()];
		const outDeprtTime = outDeprtTm ? `${outDeprtTm.slice(0, 2)}:${outDeprtTm.slice(2)}` : "";
		const inArrvTime = inArrvTm ? `${inArrvTm.slice(0, 2)}:${inArrvTm.slice(2)}` : "";
		const information =
			`${dayjs(evStartDt).format("YYYY.MM.DD")};(${dayOfWeekStart});${outDeprtTime};` +
			`${dayjs(evArriveDt).format("YYYY.MM.DD")};(${dayOfWeekArrive});${inArrvTime};` +
			`${trafficCode};${trTp ? trCompanyNm : "교통불포함"}`;

		try {
			const result = await useConfigureAxios({
				url: `${publicRuntimeConfig.ENV_ESSENTIAL_API_BASEPATH}/api/user/recentlyViewed`,
				method: "POST",
				data: {
					imgUrl,
					goodsId,
					goodsName,
					information,
					type,
					viewDate,
					userId,
					detailedUrl,
					webCustId,
					category,
					soldoutYn,
				},
			});

			if (result.data.code === "0000") queryClient.refetchQueries({queryKey: ["useGetRecently"], type: "active"});
		} catch (e) {
			console.log(e);
		}
	};

	useEffect(() => {
		if (!signin.isFetching) {
			setRecently(eventDetail, goodsImage, webCustNo);
		}
	}, [signin]);

	// tagging load
	useTaggingLoad("pkg_view_dtl", eventDetail);

	// tagging 찜하기
	const {taggingClick} = useTaggingClick("pkg_select_evCd_wish", eventDetail);

	return (
		<>
			{/* header type 4 */}
			<Header
				notFixed
				title={`${mmdd.substring(0, 2)}월 ${mmdd.substring(2, 4)}일 출발`}
				tools={["wish", "share"]}
				pageProps={{evCd, goodsCd, dspSid, menu}}
				wishAddConfirm={() => taggingClick({wishYn: "Y"})}
				wishRemoveConfirm={() => taggingClick({wishYn: "N"})}
			/>
			{/* 행사이름, 블릿, 행사 이미지, 픽토그램, 우수상품, 리뷰서머리 등.. */}
			<DetailHeader {...props} />
			{/* 여행 주요 일정 */}
			<DetailScheduleSummary {...props} />
			{/* 배너 */}
			<DetailBanner {...props} />
			{/* 상품가 */}
			<DetailPrice {...props} />
			{/* 사용가능 쿠폰 */}
			<DetailCoupon {...props} />
			{/* 일정탭 */}
			<DetailSchedule {...props} />
			{/* 기획전 */}
			<DetailPromotion {...props} />
			{/* 추천상품 */}
			<DetailRecommendProduct {...props} />
			{/* 문의, 출발일 변경, 예약하기  */}
			<DetailSNB {...props} />
			{/* 푸터 */}
			<Footer />
			{/* 행사 상세 접근 braze */}
			{/* <TaggingViewBraze goodsDetail={props.goodsDetail} eventDetail={props.eventDetail} /> */}
			<ErrorBoundary>
				<TaggingGTM {...props} />
			</ErrorBoundary>
		</>
	);
};

const getServerSidePropsSSR = async (evCd: string) => {
	const goodsCd = evCd.substring(0, 7);

	const eventDetailPromise = await eventDetailSSR(evCd);
	const eventDetail = eventDetailPromise.data.body || null;

	const goodsDetailPromise = await goodsDetailSSR(goodsCd);
	const goodsDetail = goodsDetailPromise.data.body || null;

	const goodsImagePromise = await goodsImageSSR(goodsCd);
	const goodsImage = goodsImagePromise.data.body || [];

	const goodsVideoPromise = await goodsVideoSSR(goodsCd);
	const goodsVideo = goodsVideoPromise.data.body || [];
	return {
		goodsImage,
		goodsVideo,
		eventDetail,
		goodsDetail,
	};
};

// console.log(ctx.query);
export async function getServerSideProps(ctx: GetServerSidePropsContext) {
	// SEO
	let seo;
	let data: any = {};
	const message = "판매 종료된 상품입니다. 메인 페이지로 이동합니다.";

	try {
		const {query} = ctx;
		const dspSid = (query?.dspSid as string) ?? "";
		const pageQuery: TDetailPackage["query"] = {...(query as TDetailPackage["query"])};
		if (!pageQuery.evCd && !pageQuery?.goodsCd)
			return {
				props: {
					message,
				},
			};
		if (!pageQuery?.evCd) {
			if (pageQuery.goodsMonth) {
				const lowestPriceEventRes = await goodsLowestPriceEventSSR(pageQuery.goodsCd, pageQuery.goodsMonth);
				pageQuery.evCd = lowestPriceEventRes.data.body.evCd;
			} else {
				const availEventRes = await goodsRecentEventSSR(pageQuery.goodsCd, "amt", pageQuery.startDate);
				pageQuery.evCd = availEventRes.data.body.evCd;
			}
			data = await getServerSidePropsSSR(pageQuery.evCd);
			seo = await getSEO(dspSid, "goodsCd", pageQuery.goodsCd);
			if (!data.goodsDetail)
				return {
					props: {
						message,
					},
				};
		} else {
			data = await getServerSidePropsSSR(pageQuery.evCd);
			seo = await getSEO(dspSid, "evCd", pageQuery.evCd);
			if (!data.eventDetail || !data.goodsDetail) {
				return {
					props: {
						message,
						...seo,
					},
				};
			}
		}
		return {
			props: {
				query: pageQuery,
				...seo,
				...data,
			},
		};
	} catch (error) {
		return {
			props: {
				message,
			},
		};
	}
}

export default Page;
